import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Button, Badge, Flex, Box, Text } from '@radix-ui/themes';
import { DeviceProvider, useDevices } from './contexts/DeviceContext';
import { RigProvider } from './contexts/RigContext';
import { TemplateProvider, useTemplates } from './contexts/TemplateContext';
import { ThemeProvider } from './components/ThemeProvider';
import { Device } from './types';
import DeviceForm from './components/DeviceForm';
import DeviceList from './components/DeviceList';
import PowerVisualization from './components/PowerVisualization';
import ImportExport from './components/ImportExport';
import RigTabs from './components/RigTabs';
import AdminPage from './pages/AdminPage';
import { api } from './services/api';
import { trackEvent, AnalyticsEvents } from './services/analytics';
import './App.css';

const AppContent: React.FC = () => {
  const { devices, validateConnections } = useDevices();
  const { templates, loading, error } = useTemplates();
  const [showForm, setShowForm] = useState(false);
  const [editingDevice, setEditingDevice] = useState<Device | undefined>(undefined);
  const [apiConnected, setApiConnected] = useState<boolean | null>(null);

  // Check API health on mount and track app load
  useEffect(() => {
    api.health.check().then(setApiConnected);
    trackEvent({ eventType: AnalyticsEvents.APP_LOADED });
  }, []);

  const validation = validateConnections();
  const issues = validation.errors;

  const handleAddDevice = useCallback(() => {
    setEditingDevice(undefined);
    setShowForm(true);
  }, []);

  const handleEditDevice = useCallback((device: Device) => {
    setEditingDevice(device);
    setShowForm(true);
  }, []);

  const handleCloseForm = useCallback(() => {
    setShowForm(false);
    setEditingDevice(undefined);
  }, []);

  const hasDevices = devices.length > 0;

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <Flex align="center" gap="3">
            <h1>🔭 AstroPower Manager</h1>
            {apiConnected === false && (
              <Badge color="red" size="2">API Offline</Badge>
            )}
            {apiConnected === true && (
              <Badge color="green" size="2">Connected</Badge>
            )}
            {loading && (
              <Badge color="blue" size="2">Loading...</Badge>
            )}
            {error && (
              <Badge color="orange" size="2">Error: {error}</Badge>
            )}
            {!loading && !error && templates.length > 0 && (
              <Badge color="violet" size="2">{templates.length} Templates</Badge>
            )}
          </Flex>
          <p>Visualize and manage power for your astrophotography rig (worst-case planning)</p>
        </div>
      </header>

      <main className="app-main">
        <RigTabs>
          {!hasDevices ? (
            <Box p="6" style={{ textAlign: 'center', border: '2px dashed var(--gray-6)', borderRadius: 'var(--radius-3)', backgroundColor: 'var(--gray-2)' }}>
              <Text size="5" weight="bold" mb="2" style={{ display: 'block' }}>
                🚀 Start Building Your Rig
              </Text>
              <Text size="3" color="gray" mb="4" style={{ display: 'block' }}>
                Add your first device to begin power planning
              </Text>
              <Button size="3" onClick={handleAddDevice}>
                + Add Device
              </Button>
            </Box>
          ) : (
            <>
              <Flex justify="end" gap="2" mb="3">
                <ImportExport />
                <Button variant="solid" onClick={handleAddDevice}>
                  + Add Device
                </Button>
              </Flex>
              <PowerVisualization onEditDevice={handleEditDevice} issues={issues} />
              <DeviceList onEditDevice={handleEditDevice} issues={issues} />
            </>
          )}
        </RigTabs>
      </main>

      {showForm && (
        <DeviceForm device={editingDevice} onClose={handleCloseForm} />
      )}
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <TemplateProvider>
        <RigProvider>
          <DeviceProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<AppContent />} />
                <Route path="/admin" element={<AdminPage />} />
              </Routes>
            </BrowserRouter>
          </DeviceProvider>
        </RigProvider>
      </TemplateProvider>
    </ThemeProvider>
  );
}

export default App;
